/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { BuilderNavigationService, BuilderUserContext } from '@gh/builder-shared';
import { BuilderPermission, BuilderPermissionSet, SystemRoleBit } from '@gh/builder-shared-data';
import { EntityInputDialogConfig } from '@gh/common-internal-shared';
import { getRouteInitData } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { DialogService, NotificationService, WaitingService, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { filter$, switchMap$ } from '@gh/rx/operators';

import { assign } from 'lodash';

import { OfficeRoleListBusinessService } from '../../resources/office-role-list-business.service';
import { OfficeRole } from '../../resources/office-role.model';
import { officeRoleDialog } from './office-role-details-dialog.route';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';

@Component({
  selector: 'gh-office-role-list-tab',
  template: require('./office-role-list-tab.component.html'),
})
export class OfficeRoleListTabComponent implements OnInit {
  officeRoles: OfficeRole[];
  showDeleted: boolean;
  constructor(private route: ActivatedRoute,
              private userContext: BuilderUserContext,
              private waitingService: WaitingService,
              private dialogService: DialogService,
              private notificationService: NotificationService,
              private navigationService: BuilderNavigationService,
              private officeRoleBusiness: OfficeRoleListBusinessService) {
  }

  ngOnInit(): void {
    assign(this, getRouteInitData(this.route));
  }

  loadData(): void {
    this.officeRoleBusiness.findAll(this.showDeleted).pipe(
      (this.waitingService.operator()))
      .subscribe((officeRoles: OfficeRole[]) => {
        this.officeRoles = officeRoles;
      });
  }

  onCreate(): void {
    // tslint:disable-next-line:no-magic-numbers
    if (this.officeRoles.length >= 25) {
      this.notificationService.error('msg_office_role_limit');
      return;
    }

    this.dialogService.openWithInput(officeRoleDialog, <EntityInputDialogConfig<OfficeRole>>{
      titleId: 'lbl_office_role',
      messageId: 'lbl_office_role_create',
      placeholderId: 'lbl_name',
      field: 'name',
      required: true,
      maxLength: 32,
      permissionEdit: BuilderPermission.Setup.Manager.Roles.CREATE,
      onSave: (role: OfficeRole) => this.officeRoleBusiness.create(role),
    }).afterClosed().pipe(
      filter$((role: OfficeRole) => !!role))
      .subscribe(() => {
        this.notificationService.success('msg_created_successfully');
        this.loadData();
      });
  }

  onEdit(role: OfficeRole): void {
    if (!this.canBeEdited(role)) {
      return;
    }
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Manager.Roles.EDIT_VIEW)) {
      this.dialogService.openWithInput(officeRoleDialog, <EntityInputDialogConfig<OfficeRole>>{
        titleId: 'lbl_office_role',
        messageId: 'lbl_office_role_edit',
        placeholderId: 'lbl_name',
        field: 'name',
        required: true,
        maxLength: 32,
        permissionEdit: BuilderPermission.Setup.Manager.Roles.EDIT,
        id: role.id,
        onSave: (officeRole: OfficeRole) => this.officeRoleBusiness.update(officeRole),
      }).afterClosed().pipe(
        filter$((officeRole: OfficeRole) => !!officeRole))
        .subscribe(() => {
          this.notificationService.success('msg_saved_successfully');
          this.loadData();
        });
    }
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : undefined,
      entityType: entityType ? entityType : 'OFFICE_ROLE',
      label: label ? label : 'lbl_office_roles',
    });
  }

  onDelete(role: OfficeRole): void {
    this.officeRoleBusiness.checkUsage(role).subscribe((used) => {
      this.dialogService.confirmation(yesNoConfirmation(
        used ? 'msg_office_role_mark_as_deleted' : 'msg_office_role_delete',
        { name: role.name })).pipe(
          filter$(yesAnswer),
          switchMap$(() => this.officeRoleBusiness.delete(role).pipe(
            this.waitingService.operator<void>())))
          .subscribe(
            () => {
               if (this.showDeleted && used) {
                 role.active = false;
               } else {
                 this.officeRoles = this.officeRoles.filter((officeRole) =>  officeRole.id !== role.id);
               }
             },
            this.notificationService.processAll);
    });
  }

  onUnDelete(role: OfficeRole): void {
    this.dialogService.confirmation(yesNoConfirmation(
      'msg_office_role_undelete',
      { name: role.name })).pipe(
      filter$(yesAnswer),
      switchMap$(() => this.officeRoleBusiness.undelete(role).pipe(
        this.waitingService.operator<void>())))
      .subscribe(
        () => { role.active = true; },
        this.notificationService.processAll);
  }

  onChangeShowDeleted(data: MatSlideToggleChange): void {
    this.showDeleted = data.checked;
    this.loadData();
  }

  canBeEdited(role: OfficeRole): boolean {
    return (
      this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasPermission(BuilderPermission.Setup.Manager.Roles.EDIT))
      && !!role.active;
  }

  canBeDeleted(role: OfficeRole): boolean {
    return this.canBeEdited(role);
  }

  canBeUndeleted(role: OfficeRole): boolean {
    return (
        this.userContext.hasSystemRole(SystemRoleBit.Master)
        || this.userContext.hasSystemRole(SystemRoleBit.Corporate)
        || this.userContext.hasPermission(BuilderPermission.Setup.Manager.Roles.EDIT))
      && !role.active;
  }
}
